import type { ConnectedWallet } from "@privy-io/react-auth"
import type { Chain, WalletClient } from "viem"
import { createWalletClient, custom, getAddress } from "viem"
import { breakoutChain } from "./providers"

/**
 * Find the embedded wallet from the wallet list.
 * With embedded-only architecture, this should always return a wallet.
 */
export function findEmbeddedWallet(wallets: ConnectedWallet[]): ConnectedWallet | undefined {
	return wallets.find(
		wallet =>
			"walletClientType" in wallet &&
			wallet.walletClientType === "privy" &&
			("chainType" in wallet ? wallet.chainType === "ethereum" : true),
	)
}

interface CreateWalletClientFromWalletParams {
	wallet: ConnectedWallet
	chain?: Chain
}

/**
 * Create a viem WalletClient from a Privy connected wallet.
 * For embedded wallets, Privy handles signing internally.
 */
export async function createWalletClientFromWallet({
	wallet,
	chain = breakoutChain,
}: CreateWalletClientFromWalletParams): Promise<WalletClient> {
	const provider = await wallet.getEthereumProvider()
	// getAddress validates and checksums the address, returning Address type
	const account = getAddress(wallet.address)

	// Ensure wallet is on the right chain
	if ("switchChain" in wallet && typeof wallet.switchChain === "function") {
		await wallet.switchChain(chain.id)
	}

	// Create wallet client - viem handles signTypedData via the provider
	// For Privy embedded wallets, the provider handles signing internally (silently)
	return createWalletClient({
		account,
		chain,
		transport: custom(provider),
	})
}
